import { useState } from "react";
import { FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "./ui";

const KIND = { add: "bg-emerald-100 text-emerald-800", update: "bg-sky-100 text-sky-800", delete: "bg-red-100 text-red-700" };

function Side({ v }) {
  if (!v) return <span className="text-slate-300">—</span>;
  return <div className="space-y-0.5"><div>Priority <b>{v.priority}</b> · {v.filters}</div><div className="text-slate-500">{v.products}</div></div>;
}

function Preview({ p }) {
  return (
    <div className="space-y-3" data-testid="bs-import-preview">
      <div className="flex flex-wrap gap-2 text-xs">
        {["add", "update", "delete"].map((k) => <span key={k} className={`rounded-full px-2.5 py-1 font-semibold ${KIND[k]}`} data-testid={`bs-import-count-${k}`}>{p.summary[k]} {k}</span>)}
        <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600">{p.summary.unchanged} unchanged</span>
      </div>
      {p.errors.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800" data-testid="bs-import-errors">
          <div className="mb-1 font-semibold">Fix these before anything is written:</div>
          {p.errors.map((e, i) => <div key={i}>Line {e.line}: {e.message}</div>)}
        </div>
      )}
      <div className="max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200 text-xs">
        {p.changes.map((c, i) => (
          <div key={i} className="grid grid-cols-[90px_1fr_1fr] gap-3 p-2.5" data-testid={`bs-import-change-${i}`}>
            <div><span className={`rounded px-1.5 py-0.5 font-semibold ${KIND[c.kind]}`}>{c.kind}</span><div className="mt-1 text-slate-400">{c.id ? `#${c.id}` : "new"} · line {c.line}</div></div>
            <Side v={c.before} /><Side v={c.after} />
          </div>
        ))}
        {!p.changes.length && <p className="p-4 text-center text-slate-500">Nothing to change.</p>}
      </div>
    </div>
  );
}

export function ImportDialog({ onClose, onDone }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { const { data } = await api.post("/billing-sync/mappings/import/preview", fd); setP(data); }
    catch (e) { toast.error(errMsg(e, "Could not read the file")); } finally { setBusy(false); }
  };
  const apply = async () => {
    const n = p.summary.add + p.summary.update + p.summary.delete;
    if (!window.confirm(`Write ${n} change(s) to the live billing table?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/billing-sync/mappings/import/apply", { token: p.token }); toast.success(`${data.applied} change(s) written`); onDone(); }
    catch (e) { toast.error(errMsg(e, "Import failed — nothing was written")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="bs-import-dialog">
        <DialogHeader>
          <DialogTitle>Import rules from CSV</DialogTitle>
          <DialogDescription>Use a file from Export CSV. Blank id = new rule, an id = update that rule, “yes” in the delete column removes it. Rules not in the file are left alone. Nothing is written until you apply, and it all goes in together or not at all.</DialogDescription>
        </DialogHeader>
        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-200 px-4 py-6 text-sm text-slate-600 transition-colors hover:border-emerald-300 hover:bg-emerald-50/40">
          {busy && !p ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} {p ? "Choose another file" : "Choose a CSV file"}
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="bs-import-file" />
        </label>
        {p && <Preview p={p} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bs-import-cancel">Cancel</Button>
          <Button onClick={apply} disabled={busy || !p?.token} data-testid="bs-import-apply">{busy && p && <Loader2 className="h-4 w-4 animate-spin" />} Apply to billing table</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
